import { Injectable } from '@angular/core';
import { Observable, Subject, timer, throwError, Subscription } from 'rxjs';
import { switchMap, tap, catchError, finalize } from 'rxjs/operators';
import OpenAI from 'openai';
import { NotificationService } from '../notification-service/notification.service';
import { TaskCancellationError } from './errors/task-cancellation.error';
import { RetryConfig, DEFAULT_AI_RETRY_CONFIG } from '../model/RetryConfig';
import {
	PollingStrategy,
	OPENAI_POLLING_STRATEGY,
} from '../model/PollingStrategy';
import { TaskLifecycleHooks } from '../model/TaskLifecycleHooks';

export interface QueuedTask<T = any> {
	id: string;
	request: () => Observable<T>; // Initial request function
	subject: Subject<T>;
	retryCount: number;
	validationRetryCount: number; // Separate counter for validation retries
	status: 'queued' | 'active' | 'polling' | 'completed' | 'failed';
	startTime?: number;
	retryConfig: RetryConfig; // Retry configuration for this task
	responseValidator?: (response: T) => boolean; // Optional validator function (returns true if valid)
	lifecycleHooks?: TaskLifecycleHooks<T>; // Optional lifecycle event callbacks

	// Optional polling configuration for long-running operations
	polling?: {
		pollRequest: (responseId: string) => Observable<T>; // Polling function
		strategy: PollingStrategy<T>; // Polling strategy (intervals, max attempts, etc.)
		responseId?: string; // Response ID extracted from initial response
		pollCount: number; // Current polling attempt count
		subscription?: Subscription; // Active polling subscription for cancellation
	};
}

export interface QueueStatus {
	queuedTasks: number;
	activeTasks: number;
	completedTasks: number;
	failedTasks: number;
}

@Injectable({
	providedIn: 'root',
})
export class BackgroundTaskQueueService {
	private queue: QueuedTask[] = [];
	private activeTasks: Map<string, QueuedTask> = new Map();
	private completedTasks: Map<string, QueuedTask> = new Map();
	private failedTasks: Map<string, QueuedTask> = new Map();

	// Track active polling subscriptions by taskId for proper cancellation
	private pollingSubscriptions: Map<string, Subscription> = new Map();

	// Debug counters for subscription tracking
	private subscriptionCreatedCount = 0;
	private subscriptionCancelledCount = 0;

	private readonly MAX_CONCURRENT = 9;
	private readonly POLL_INTERVAL = 6000;
	private readonly MAX_POLL_ATTEMPTS = 75; // 5 minutes max (75 * 4 seconds)
	private readonly MAX_RETRIES = 2;

	// Observable for monitoring queue status
	private queueStatusSubject = new Subject<QueueStatus>();
	public queueStatus$ = this.queueStatusSubject.asObservable();

	// Subject that completes when all work is done (queue empty AND no active tasks)
	private allWorkCompleteSubject: Subject<void> | null = null;

	private taskIdCounter = 0;

	constructor(private notificationService: NotificationService) {
		console.log('BackgroundTaskQueueService initialized');
	}

	/**
	 * Add a task to the queue or start it immediately if under the concurrent limit
	 * @param request Function that returns the initial request observable
	 * @param pollRequest Optional function that polls for status using response ID
	 * @param retryConfig Optional retry configuration (defaults to DEFAULT_AI_RETRY_CONFIG)
	 * @param responseValidator Optional function to validate response (returns true if valid)
	 * @param pollingStrategy Optional polling strategy (defaults to OPENAI_POLLING_STRATEGY if pollRequest provided)
	 * @param lifecycleHooks Optional lifecycle event callbacks
	 * @returns Object with taskId and observable that emits when the task completes
	 */
	public addTask<T = any>(
		request: () => Observable<T>,
		pollRequest?: (responseId: string) => Observable<T>,
		retryConfig: RetryConfig = DEFAULT_AI_RETRY_CONFIG,
		responseValidator?: (response: T) => boolean,
		pollingStrategy?: PollingStrategy<T>,
		lifecycleHooks?: TaskLifecycleHooks<T>,
	): { taskId: string; observable: Observable<T> } {
		const taskId = `task-${++this.taskIdCounter}`;
		const subject = new Subject<T>();

		const task: QueuedTask<T> = {
			id: taskId,
			request,
			subject,
			retryCount: 0,
			validationRetryCount: 0,
			status: 'queued',
			retryConfig,
			responseValidator,
			lifecycleHooks,
		};

		// Add polling configuration if pollRequest is provided
		if (pollRequest) {
			task.polling = {
				pollRequest,
				strategy: pollingStrategy || OPENAI_POLLING_STRATEGY,
				pollCount: 0,
			};
		}

		// Add to queue
		this.queue.push(task);
		console.log(
			`[QUEUE] Task ${taskId} created and added to queue. Queue length: ${this.queue.length}, Active: ${this.activeTasks.size}/${this.MAX_CONCURRENT}`,
		);
		console.log(
			`[QUEUE] Task ${taskId} config - maxRetries: ${retryConfig.maxRetries}, retryDelay: ${retryConfig.retryDelay}ms, hasValidator: ${!!responseValidator}`,
		);

		// Call onTaskCreated lifecycle hook
		if (lifecycleHooks?.onTaskCreated) {
			lifecycleHooks.onTaskCreated(taskId);
		}

		// Try to start immediately if under limit
		this.processQueue();

		// Return both the taskId and the observable
		return {
			taskId,
			observable: subject.asObservable(),
		};
	}

	/**
	 * Process the queue, starting new tasks if under the concurrent limit
	 */
	private processQueue(): void {
		while (
			this.activeTasks.size < this.MAX_CONCURRENT &&
			this.queue.length > 0
		) {
			const task = this.queue.shift();
			if (task) {
				this.startTask(task);
			}
		}

		this.emitQueueStatus();
	}

	/**
	 * Start a specific task
	 */
	private startTask(task: QueuedTask): void {
		task.status = 'active';
		task.startTime = Date.now();
		this.activeTasks.set(task.id, task);

		// Call onTaskStart lifecycle hook
		if (task.lifecycleHooks?.onTaskStart) {
			task.lifecycleHooks.onTaskStart(task.id);
		}

		// Add random jitter of 0-2000ms to distribute initial requests
		const jitter = Math.floor(Math.random() * 2000);

		console.log(
			`[TASK-START] ${task.id} starting with ${jitter}ms jitter. Active: ${this.activeTasks.size}/${this.MAX_CONCURRENT}, Queued: ${this.queue.length}`,
		);

		// Delay the initial request by the jitter amount
		timer(jitter)
			.pipe(
				switchMap(() => {
					// Execute the initial request after jitter delay
					return task.request().pipe(
						tap((response) => {
							console.debug(
								`Task ${task.id} initial response received. Status: ${(response as any).status || 'N/A'}`,
							);

							// Call onRequestComplete lifecycle hook
							if (task.lifecycleHooks?.onRequestComplete) {
								task.lifecycleHooks.onRequestComplete(
									task.id,
									response,
								);
							}

							// Check if task needs polling using polling strategy
							const needsPolling =
								task.polling?.strategy.needsPolling(response) ||
								false;

							if (needsPolling && task.polling) {
								// Extract response ID using polling strategy
								task.polling.responseId =
									task.polling.strategy.extractResponseId(
										response,
									);
								task.status = 'polling';

								// Call onPollStart lifecycle hook
								if (
									task.lifecycleHooks?.onPollStart &&
									task.polling.responseId
								) {
									task.lifecycleHooks.onPollStart(
										task.id,
										task.polling.responseId,
									);
								}

								// Start polling for this specific task
								this.pollTask(task);
							} else {
								// Task completed immediately - validate if validator is provided
								if (
									task.responseValidator &&
									task.retryConfig
										.shouldRetryOnInvalidResponse
								) {
									const isValid =
										task.responseValidator(response);

									if (!isValid) {
										// Call onValidationFailed lifecycle hook
										if (
											task.lifecycleHooks
												?.onValidationFailed
										) {
											task.lifecycleHooks.onValidationFailed(
												task.id,
												response,
												task.validationRetryCount,
											);
										}

										// Response validation failed - check if we can retry
										if (
											task.validationRetryCount <
											task.retryConfig.maxRetries
										) {
											task.validationRetryCount++;
											console.log(
												`[TASK-RETRY] ${task.id} validation failed (immediate response), retrying after ${task.retryConfig.retryDelay}ms (attempt ${task.validationRetryCount}/${task.retryConfig.maxRetries})`,
											);

											// Call onRetry lifecycle hook
											if (task.lifecycleHooks?.onRetry) {
												task.lifecycleHooks.onRetry(
													task.id,
													task.validationRetryCount,
													'validation',
												);
											}

											// Re-queue the task after delay
											this.retryTaskAfterDelay(task);
											return; // Don't complete the task yet
										} else {
											console.log(
												`[VALIDATION] ${task.id} validation failed after ${task.retryConfig.maxRetries} retries, failing task`,
											);
											this.handleTaskError(
												task,
												new Error(
													'Palvelimelta ei saatu oikeanlaista vastausta.',
												),
											);
											return; // Don't complete the task
										}
									} else {
										console.log(
											`[VALIDATION] ${task.id} validation passed`,
										);
									}
								}

								// Task completed immediately (no polling needed)
								this.completeTask(task, response);
							}
						}),
						catchError((error) => {
							console.error(
								`Task ${task.id} initial request failed:`,
								error,
							);
							this.handleTaskError(task, error);
							return throwError(() => error);
						}),
					);
				}),
			)
			.subscribe();
	}

	/**
	 * Retry a task after a delay
	 */
	private retryTaskAfterDelay(task: QueuedTask): void {
		console.log(
			`[TASK-RETRY] ${task.id} re-queuing for retry after ${task.retryConfig.retryDelay}ms delay`,
		);

		// Remove from active tasks
		this.activeTasks.delete(task.id);

		// Reset task state for retry
		task.status = 'queued';
		if (task.polling) {
			task.polling.pollCount = 0;
		}

		// Add back to front of queue for immediate retry after delay
		timer(task.retryConfig.retryDelay).subscribe(() => {
			console.log(
				`[TASK-RETRY] ${task.id} delay complete, adding to front of queue`,
			);
			this.queue.unshift(task);
			this.processQueue();
		});
	}

	/**
	 * Poll a specific task for completion
	 */
	private pollTask(task: QueuedTask): void {
		// Early exit if no polling config
		if (!task.polling) {
			console.error(
				`[POLL-DIAG] Poll aborted - task ${task.id} has no polling configuration`,
			);
			this.handleTaskError(task, new Error('No polling configuration'));
			return;
		}

		// Consolidated diagnostic for poll cycle start
		const pollDiag = {
			taskId: task.id,
			status: task.status,
			pollCount: task.polling.pollCount,
			hasResponseId: !!task.polling.responseId,
			hasPollRequest: !!task.polling.pollRequest,
			inActiveTasks: this.activeTasks.has(task.id),
			hasExistingSubscription: !!task.polling.subscription,
			timestamp: new Date().toISOString(),
		};

		console.log('[POLL-DIAG] Poll cycle starting', pollDiag);

		if (!task.polling.responseId || !task.polling.pollRequest) {
			console.error(
				'[POLL-DIAG] Poll aborted - missing requirements',
				pollDiag,
			);
			this.handleTaskError(
				task,
				new Error('Missing polling requirements'),
			);
			return;
		}

		// Check if task is still active before starting to poll
		if (task.status === 'failed' || !this.activeTasks.has(task.id)) {
			console.log('[POLL-DIAG] Poll skipped - task not active', pollDiag);
			return;
		}

		// Additional safety check - ensure task is not marked as cancelled/failed
		if (task.status !== 'active' && task.status !== 'polling') {
			console.log(
				'[POLL-DIAG] Poll skipped - unexpected status',
				pollDiag,
			);
			return;
		}

		// Cancel any existing polling subscription for this task
		if (task.polling.subscription) {
			// console.log(`QUEUE-SERVICE: Cancelling existing polling subscription for task ${task.id}`);
			task.polling.subscription.unsubscribe();
			task.polling.subscription = undefined;
			this.pollingSubscriptions.delete(task.id);
			this.subscriptionCancelledCount++;
			// console.log(`QUEUE-SERVICE: Subscription stats after existing subscription cleanup - Created: ${this.subscriptionCreatedCount}, Cancelled: ${this.subscriptionCancelledCount}, Active: ${this.pollingSubscriptions.size}`);
		}

		// Add small random jitter (0-500ms) to polling interval to prevent synchronization
		const pollJitter = Math.floor(Math.random() * 500);
		const pollInterval = task.polling.strategy.pollInterval + pollJitter;

		// Create a polling observable with its own timer
		const pollObservable = timer(pollInterval).pipe(
			switchMap(() => {
				// Check again if task is still active before making the request
				if (
					task.status === 'failed' ||
					!this.activeTasks.has(task.id)
				) {
					console.log(
						`Task ${task.id} was cancelled during polling, aborting`,
					);
					throw new Error('Task cancelled during polling');
				}

				if (!task.polling) {
					throw new Error('Polling configuration missing');
				}

				task.polling.pollCount++;
				console.debug(
					`Polling task ${task.id} (attempt ${task.polling.pollCount}/${task.polling.strategy.maxPollAttempts})`,
				);

				// Call onPollAttempt lifecycle hook
				if (task.lifecycleHooks?.onPollAttempt) {
					task.lifecycleHooks.onPollAttempt(
						task.id,
						task.polling.pollCount,
						task.polling.strategy.maxPollAttempts,
					);
				}

				// Check if we've exceeded max poll attempts
				if (
					task.polling.pollCount >=
					task.polling.strategy.maxPollAttempts
				) {
					throw new Error('Maximum polling attempts exceeded');
				}

				if (!task.polling.pollRequest || !task.polling.responseId) {
					throw new Error('Missing polling requirements');
				}
				return task.polling.pollRequest(task.polling.responseId);
			}),
			tap((response) => {
				// Check once more if task is still active before processing response
				if (
					task.status === 'failed' ||
					!this.activeTasks.has(task.id)
				) {
					console.log(
						`Task ${task.id} was cancelled, ignoring poll response`,
					);
					return;
				}

				if (!task.polling) {
					throw new Error('Polling configuration missing');
				}

				// Check if polling is complete using polling strategy
				const isComplete =
					!task.polling.strategy.needsPolling(response);

				if (isComplete) {
					console.log(
						`Task ${task.id} completed with status: ${(response as any).status}`,
					);

					// Call onPollComplete lifecycle hook
					if (task.lifecycleHooks?.onPollComplete) {
						task.lifecycleHooks.onPollComplete(task.id, response);
					}

					// Check if response contains an error before completing
					if ((response as any).error) {
						console.error(`Task ${task.id} completed with error:`, {
							code: (response as any).error.code,
							message: (response as any).error.message,
						});
						this.handleTaskError(
							task,
							new Error(
								`Response error: ${(response as any).error.message}`,
							),
						);
					} else {
						// Validate response if validator is provided
						if (
							task.responseValidator &&
							task.retryConfig.shouldRetryOnInvalidResponse
						) {
							const isValid = task.responseValidator(response);

							if (!isValid) {
								// Call onValidationFailed lifecycle hook
								if (task.lifecycleHooks?.onValidationFailed) {
									task.lifecycleHooks.onValidationFailed(
										task.id,
										response,
										task.validationRetryCount,
									);
								}

								// Response validation failed - check if we can retry
								if (
									task.validationRetryCount <
									task.retryConfig.maxRetries
								) {
									task.validationRetryCount++;
									console.log(
										`[TASK-RETRY] ${task.id} validation failed after polling, retrying (attempt ${task.validationRetryCount}/${task.retryConfig.maxRetries})`,
									);

									// Call onRetry lifecycle hook
									if (task.lifecycleHooks?.onRetry) {
										task.lifecycleHooks.onRetry(
											task.id,
											task.validationRetryCount,
											'validation',
										);
									}

									// Cancel current polling and retry entire task
									if (task.polling?.subscription) {
										task.polling.subscription.unsubscribe();
										task.polling.subscription = undefined;
										this.pollingSubscriptions.delete(
											task.id,
										);
									}

									// Re-queue the task after delay
									this.retryTaskAfterDelay(task);
									return; // Don't complete the task yet
								} else {
									console.log(
										`Task ${task.id} validation failed after ${task.retryConfig.maxRetries} retries, failing task`,
									);
									this.handleTaskError(
										task,
										new Error(
											'Palvelimelta ei saatu oikeanlaista vastausta.',
										),
									);
									return; // Don't complete the task
								}
							} else {
								console.log(
									`Task ${task.id} validation passed after polling`,
								);
							}
						}

						this.completeTask(task, response);
					}
				} else {
					// Check if task is still active before scheduling next poll (prevent recursive polling after cancellation)
					const currentTask = this.activeTasks.get(task.id);
					const nextPollDiag = {
						taskId: task.id,
						responseStatus: response.status,
						currentTaskExists: !!currentTask,
						currentTaskStatus: currentTask?.status,
						inActiveTasks: this.activeTasks.has(task.id),
						willScheduleNextPoll: false,
						timestamp: new Date().toISOString(),
					};

					if (
						!currentTask ||
						currentTask.status === 'failed' ||
						!this.activeTasks.has(task.id)
					) {
						console.log(
							'[POLL-DIAG] Next poll cancelled - task no longer active',
							nextPollDiag,
						);
						return;
					}

					// Continue polling - schedule next poll
					nextPollDiag.willScheduleNextPoll = true;
					console.log(
						'[POLL-DIAG] Scheduling next poll',
						nextPollDiag,
					);
					this.pollTask(task);
				}
			}),
			catchError((error) => {
				console.error(`Task ${task.id} polling error:`, error);
				this.handleTaskError(task, error);
				return throwError(() => error);
			}),
			finalize(() => {
				// Clean up subscription reference when polling finishes
				if (task.polling?.subscription) {
					this.pollingSubscriptions.delete(task.id);
					task.polling.subscription = undefined;
				}
			}),
		);

		// Store the subscription for cancellation
		const subscription = pollObservable.subscribe();
		if (task.polling) {
			task.polling.subscription = subscription;
			this.pollingSubscriptions.set(task.id, subscription);
			this.subscriptionCreatedCount++;
		}

		console.debug(
			`QUEUE-SERVICE: Created polling subscription for task ${task.id}`,
		);
		console.debug(
			`QUEUE-SERVICE: Subscription stats - Created: ${this.subscriptionCreatedCount}, Cancelled: ${this.subscriptionCancelledCount}, Active: ${this.pollingSubscriptions.size}`,
		);
	}

	/**
	 * Complete a task successfully
	 */
	private completeTask<T = any>(task: QueuedTask<T>, response: T): void {
		task.status = 'completed';
		const duration = task.startTime ? Date.now() - task.startTime : 0;

		console.log(
			`[TASK-COMPLETE] ${task.id} completed successfully in ${duration}ms with ${task.polling?.pollCount || 0} polls. Retry count: ${task.retryCount}, Validation retry count: ${task.validationRetryCount}`,
		);

		// Clean up polling subscription if it exists
		if (task.polling?.subscription) {
			console.debug(
				`QUEUE-SERVICE: Cleaning up polling subscription for completed task ${task.id}`,
			);
			task.polling.subscription.unsubscribe();
			task.polling.subscription = undefined;
			this.pollingSubscriptions.delete(task.id);
			this.subscriptionCancelledCount++;
			console.debug(
				`QUEUE-SERVICE: Subscription stats after completion - Created: ${this.subscriptionCreatedCount}, Cancelled: ${this.subscriptionCancelledCount}, Active: ${this.pollingSubscriptions.size}`,
			);
		}

		// Move from active to completed BEFORE completing the subject
		// This ensures all cleanup and queue processing happens before the observable completes
		this.activeTasks.delete(task.id);
		this.completedTasks.set(task.id, task);

		// Process next task in queue BEFORE completing the subject
		// This ensures queue status is logged before any downstream handlers fire
		this.processQueue();

		// Call onTaskComplete lifecycle hook
		if (task.lifecycleHooks?.onTaskComplete) {
			task.lifecycleHooks.onTaskComplete(task.id, response, duration);
		}

		// Emit the response and complete the subject LAST
		// This triggers any downstream handlers (like forkJoin) after all background work is done
		task.subject.next(response);
		task.subject.complete();
	}

	/**
	 * Handle task error, with retry logic based on RetryConfig
	 */
	private handleTaskError(task: QueuedTask, error: unknown): void {
		// Log error details
		const errorMessage =
			error instanceof Error ? error.message : String(error);
		console.log(
			`[TASK-ERROR] ${task.id} encountered error: ${errorMessage}`,
		);

		// Clean up polling subscription if it exists
		if (task.polling?.subscription) {
			task.polling.subscription.unsubscribe();
			task.polling.subscription = undefined;
			this.pollingSubscriptions.delete(task.id);
			this.subscriptionCancelledCount++;
		}

		// Check if this is a user cancellation error - don't retry if configured
		const isCancellation = TaskCancellationError.isCancellationError(error);
		if (isCancellation && task.retryConfig.excludeCancellation) {
			console.log(
				`[TASK-ERROR] ${task.id} cancelled by user, not retrying`,
			);

			// Call onTaskError lifecycle hook
			if (task.lifecycleHooks?.onTaskError) {
				task.lifecycleHooks.onTaskError(task.id, error);
			}

			task.status = 'failed';
			task.subject.error(error);
			this.activeTasks.delete(task.id);
			this.failedTasks.set(task.id, task);
			this.processQueue();
			return;
		}

		// Check if we should retry on errors
		if (!task.retryConfig.shouldRetryOnError) {
			console.log(
				`[TASK-ERROR] ${task.id} failed but retry on error is disabled`,
			);

			// Call onTaskError lifecycle hook
			if (task.lifecycleHooks?.onTaskError) {
				task.lifecycleHooks.onTaskError(task.id, error);
			}

			task.status = 'failed';
			task.subject.error(error);
			this.activeTasks.delete(task.id);
			this.failedTasks.set(task.id, task);
			this.processQueue();
			return;
		}

		task.retryCount++;

		if (task.retryCount <= task.retryConfig.maxRetries) {
			console.log(
				`[TASK-RETRY] ${task.id} retrying due to error (attempt ${task.retryCount}/${task.retryConfig.maxRetries}) after ${task.retryConfig.retryDelay}ms delay`,
			);

			// Call onRetry lifecycle hook
			if (task.lifecycleHooks?.onRetry) {
				task.lifecycleHooks.onRetry(task.id, task.retryCount, 'error');
			}

			// Reset task state
			task.status = 'queued';
			if (task.polling) {
				task.polling.pollCount = 0;
			}
			this.activeTasks.delete(task.id);

			// Re-add to front of queue for immediate retry
			this.queue.unshift(task);

			// Process queue after configured delay
			timer(task.retryConfig.retryDelay).subscribe(() =>
				this.processQueue(),
			);
		} else {
			console.error(
				`[TASK-ERROR] ${task.id} failed permanently after ${task.retryConfig.maxRetries} retries. Error: ${errorMessage}`,
			);

			// Call onTaskError lifecycle hook
			if (task.lifecycleHooks?.onTaskError) {
				task.lifecycleHooks.onTaskError(task.id, error);
			}

			task.status = 'failed';
			task.subject.error(error);

			// Move from active to failed
			this.activeTasks.delete(task.id);
			this.failedTasks.set(task.id, task);

			// Process next task in queue
			this.processQueue();
		}
	}

	/**
	 * Emit current queue status
	 */
	private emitQueueStatus(): void {
		const status: QueueStatus = {
			queuedTasks: this.queue.length,
			activeTasks: this.activeTasks.size,
			completedTasks: this.completedTasks.size,
			failedTasks: this.failedTasks.size,
		};

		this.queueStatusSubject.next(status);

		// Reduce logging noise - only log when there's meaningful queue activity
		// Skip logging when queue is fully drained (nothing queued, nothing active)
		if (status.queuedTasks > 0 || status.activeTasks > 0) {
			console.log(
				`Queue Status - Queued: ${status.queuedTasks}, Active: ${status.activeTasks}, Completed: ${status.completedTasks}, Failed: ${status.failedTasks}`,
			);
		}
	}

	/**
	 * Get current queue status
	 */
	public getQueueStatus(): QueueStatus {
		return {
			queuedTasks: this.queue.length,
			activeTasks: this.activeTasks.size,
			completedTasks: this.completedTasks.size,
			failedTasks: this.failedTasks.size,
		};
	}

	/**
	 * Get subscription tracking statistics for debugging
	 */
	public getSubscriptionStats(): {
		created: number;
		cancelled: number;
		active: number;
	} {
		return {
			created: this.subscriptionCreatedCount,
			cancelled: this.subscriptionCancelledCount,
			active: this.pollingSubscriptions.size,
		};
	}

	/**
	 * Clear completed and failed tasks from memory
	 */
	public clearHistory(): void {
		this.completedTasks.clear();
		this.failedTasks.clear();
		this.emitQueueStatus();
	}

	/**
	 * Check if all work is complete and notify if so
	 * Called after each task completes or fails
	 */
	private checkAndNotifyAllWorkComplete(): void {
		// Only check if we have an active monitor
		if (
			!this.allWorkCompleteSubject ||
			this.allWorkCompleteSubject.closed
		) {
			return;
		}

		// Check if all work is done
		if (this.queue.length === 0 && this.activeTasks.size === 0) {
			console.log(
				`[QUEUE-DRAIN] All work complete! Completed: ${this.completedTasks.size}, Failed: ${this.failedTasks.size}`,
			);
			this.allWorkCompleteSubject.complete();
			this.allWorkCompleteSubject = null;
		}
	}

	/**
	 * Cancel a specific task by taskId (works for both queued and active tasks)
	 */
	public cancelTask(taskId: string): boolean {
		// Collect diagnostic data
		const diagnostics = {
			taskId: taskId,
			timestamp: new Date().toISOString(),
			queueStateBefore: {
				queuedTasks: this.queue.length,
				activeTasks: this.activeTasks.size,
				activePollingSubscriptions: this.pollingSubscriptions.size,
				subscriptionStats: {
					created: this.subscriptionCreatedCount,
					cancelled: this.subscriptionCancelledCount,
				},
			},
			taskLocation: 'not-found' as 'queued' | 'active' | 'not-found',
			taskStatus: undefined as string | undefined,
			hadPollingSubscription: false,
			unsubscribeSuccess: false,
			unsubscribeError: undefined as unknown,
			errorEmitSuccess: false,
			errorEmitError: undefined as unknown,
			queueStateAfter: {} as {
				queuedTasks: number;
				activeTasks: number;
				activePollingSubscriptions: number;
			},
			result: false,
			availableQueuedTaskIds: [] as string[],
			availableActiveTaskIds: [] as string[],
		};

		// First check if it's in the queue (not yet started)
		const queueIndex = this.queue.findIndex((task) => task.id === taskId);
		if (queueIndex !== -1) {
			const task = this.queue.splice(queueIndex, 1)[0];
			diagnostics.taskLocation = 'queued';
			diagnostics.taskStatus = task.status;

			task.status = 'failed';
			task.subject.error(new TaskCancellationError(task.id));
			this.failedTasks.set(task.id, task);

			diagnostics.queueStateAfter = {
				queuedTasks: this.queue.length,
				activeTasks: this.activeTasks.size,
				activePollingSubscriptions: this.pollingSubscriptions.size,
			};
			diagnostics.result = true;

			console.log(
				'[CANCEL-DIAG] cancelTask() completed - queued task',
				diagnostics,
			);
			this.emitQueueStatus();
			return true;
		}

		// Check if it's an active task
		const activeTask = this.activeTasks.get(taskId);
		if (activeTask) {
			diagnostics.taskLocation = 'active';
			diagnostics.taskStatus = activeTask.status;
			diagnostics.hadPollingSubscription =
				!!activeTask.polling?.subscription;

			// Cancel any active polling subscription for this task
			if (activeTask.polling?.subscription) {
				try {
					activeTask.polling.subscription.unsubscribe();
					this.subscriptionCancelledCount++;
					diagnostics.unsubscribeSuccess = true;
				} catch (error) {
					diagnostics.unsubscribeError = error;
				}

				activeTask.polling.subscription = undefined;
				this.pollingSubscriptions.delete(taskId);
			}

			// Mark task as cancelled and emit error
			activeTask.status = 'failed';
			try {
				activeTask.subject.error(
					new TaskCancellationError(activeTask.id),
				);
				diagnostics.errorEmitSuccess = true;
			} catch (error) {
				diagnostics.errorEmitError = error;
			}

			// Remove from active tasks
			this.activeTasks.delete(taskId);
			this.failedTasks.set(taskId, activeTask);

			diagnostics.queueStateAfter = {
				queuedTasks: this.queue.length,
				activeTasks: this.activeTasks.size,
				activePollingSubscriptions: this.pollingSubscriptions.size,
			};
			diagnostics.result = true;

			console.log(
				'[CANCEL-DIAG] cancelTask() completed - active task',
				diagnostics,
			);
			this.emitQueueStatus();

			// Process next task in queue
			this.processQueue();

			return true;
		}

		// Task not found
		diagnostics.availableQueuedTaskIds = this.queue.map((t) => t.id);
		diagnostics.availableActiveTaskIds = Array.from(
			this.activeTasks.keys(),
		);

		console.warn(
			'[CANCEL-DIAG] cancelTask() - task not found',
			diagnostics,
		);
		return false;
	}

	/**
	 * Cancel a specific active task by responseId (kept for backward compatibility)
	 */
	public cancelActiveTask(responseId: string): boolean {
		console.log(
			`Attempting to cancel active task with responseId: ${responseId}`,
		);

		// Find the task with matching responseId
		let taskToCancel: QueuedTask | undefined;
		for (const [taskId, task] of this.activeTasks) {
			if (task.polling?.responseId === responseId) {
				taskToCancel = task;
				console.log(
					`Found active task ${taskId} with matching responseId`,
				);
				break;
			}
		}

		if (taskToCancel) {
			return this.cancelTask(taskToCancel.id);
		} else {
			console.log(`No active task found with responseId: ${responseId}`);
			return false;
		}
	}

	/**
	 * Cancel all pending tasks
	 */
	public cancelAllPendingTasks(): void {
		// Show warning toast as the backend functionality to cancel active tasks is not implemented yet
		this.notificationService.ShowToast(
			'Ominaisuutta ei ole vielä toteutettu.',
			'warning',
		);

		// Cancel queued tasks (this part works)
		const cancelledCount = this.queue.length;
		while (this.queue.length > 0) {
			const task = this.queue.shift();
			if (task) {
				task.subject.error(new Error('Task cancelled'));
			}
		}

		console.log(
			`Cancelled ${cancelledCount} queued tasks. Active tasks (${this.activeTasks.size}) will continue to completion.`,
		);
		console.log(
			'TODO: Backend implementation needed to cancel active OpenAI requests',
		);

		this.emitQueueStatus();
	}
}
